import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { participantsApi, type Device, type TaskTemplate } from "../api/participants";
import { qk } from "../../../../lib/queryKeys";
import { SectionCard } from "../../settings/components/SectionCard";

// ---------------------------------------------------------------------------
// Device dialog
// ---------------------------------------------------------------------------

interface DeviceDialogProps {
  open: boolean;
  initial?: Device | null;
  onClose: () => void;
  onSave: (payload: { name: string; type: string | null; active: boolean }) => void;
  saving: boolean;
}

const DeviceDialog: React.FC<DeviceDialogProps> = ({ open, initial, onClose, onSave, saving }) => {
  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState(initial?.type ?? "");
  const [active, setActive] = useState(initial?.active !== false);

  React.useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setType(initial?.type ?? "");
      setActive(initial?.active !== false);
    }
  }, [open, initial]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{initial ? "Edit device" : "Add device"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Type (optional)"
            value={type}
            onChange={(e) => setType(e.target.value)}
            size="small"
            fullWidth
            helperText="e.g. Wearable, Smart watch"
          />
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || saving}
          onClick={() => onSave({ name: name.trim(), type: type.trim() || null, active })}
        >
          {saving ? <CircularProgress size={18} /> : initial ? "Save" : "Add device"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Template dialog
// ---------------------------------------------------------------------------

interface TemplateDialogProps {
  open: boolean;
  initial?: TaskTemplate | null;
  onClose: () => void;
  onSave: (payload: { name: string; description: string | null; category: string | null; active: boolean; sort_order: number }) => void;
  saving: boolean;
}

const TemplateDialog: React.FC<TemplateDialogProps> = ({ open, initial, onClose, onSave, saving }) => {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [category, setCategory] = useState(initial?.category ?? "");
  const [active, setActive] = useState(initial?.active !== false);
  const [sortOrder, setSortOrder] = useState(initial?.sort_order ?? 0);

  React.useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setDescription(initial?.description ?? "");
      setCategory(initial?.category ?? "");
      setActive(initial?.active !== false);
      setSortOrder(initial?.sort_order ?? 0);
    }
  }, [open, initial]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{initial ? "Edit template" : "Add template"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Category (optional)"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Sort order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              size="small"
              sx={{ width: 100 }}
            />
          </Stack>
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || saving}
          onClick={() => onSave({ name: name.trim(), description: description.trim() || null, category: category.trim() || null, active, sort_order: sortOrder })}
        >
          {saving ? <CircularProgress size={18} /> : initial ? "Save" : "Add template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ParticipantSettings: React.FC = () => {
  const queryClient = useQueryClient();

  const [deviceSearch, setDeviceSearch] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(null);

  const devicesQuery = useQuery({
    queryKey: qk.devices(),
    queryFn: () => participantsApi.listDevices(),
  });
  const templatesQuery = useQuery({
    queryKey: qk.taskTemplates(),
    queryFn: () => participantsApi.listTaskTemplates(),
  });

  const devices = devicesQuery.data?.data ?? [];
  const templates = templatesQuery.data?.data ?? [];

  const createDeviceMutation = useMutation({
    mutationFn: (payload: Partial<Device>) => participantsApi.createDevice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.devices() });
      setDeviceDialogOpen(false);
    },
  });

  const updateDeviceMutation = useMutation({
    mutationFn: ({ deviceId, payload }: { deviceId: string; payload: Partial<Device> }) =>
      participantsApi.updateDevice(deviceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.devices() });
      setDeviceDialogOpen(false);
      setEditingDevice(null);
    },
  });

  const deleteDeviceMutation = useMutation({
    mutationFn: (deviceId: string) => participantsApi.deleteDevice(deviceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.devices() }),
  });

  const createTemplateMutation = useMutation({
    mutationFn: (payload: Partial<TaskTemplate>) => participantsApi.createTaskTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.taskTemplates() });
      setTemplateDialogOpen(false);
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ templateId, payload }: { templateId: string; payload: Partial<TaskTemplate> }) =>
      participantsApi.updateTaskTemplate(templateId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.taskTemplates() });
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId: string) => participantsApi.deleteTaskTemplate(templateId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.taskTemplates() }),
  });

  const filteredDevices = useMemo(() => {
    if (!deviceSearch) return devices;
    const q = deviceSearch.toLowerCase();
    return devices.filter((d) => d.name.toLowerCase().includes(q) || (d.type ?? "").toLowerCase().includes(q));
  }, [devices, deviceSearch]);

  const filteredTemplates = useMemo(() => {
    if (!templateSearch) return templates;
    const q = templateSearch.toLowerCase();
    return templates.filter((t) => t.name.toLowerCase().includes(q) || (t.category ?? "").toLowerCase().includes(q) || (t.description ?? "").toLowerCase().includes(q));
  }, [templates, templateSearch]);

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700}>Participant Settings</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Devices */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <SectionCard
            title="Devices"
            description={`${devices.length} device${devices.length !== 1 ? "s" : ""}`}
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => { setEditingDevice(null); setDeviceDialogOpen(true); }}
              >
                Add device
              </Button>
            }
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search devices…"
              value={deviceSearch}
              onChange={(e) => setDeviceSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
                endAdornment: deviceSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setDeviceSearch("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ mb: 2 }}
            />

            <Divider />

            {devicesQuery.isLoading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Stack>
            ) : devicesQuery.isError ? (
              <Alert severity="error">Failed to load devices.</Alert>
            ) : devices.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                <DevicesOtherIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2">No devices yet.</Typography>
                <Typography variant="caption">Click "Add device" to create one.</Typography>
              </Box>
            ) : filteredDevices.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">No results for "{deviceSearch}"</Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />} spacing={0}>
                {filteredDevices.map((device) => (
                  <Stack
                    key={device.id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ py: 1.25 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {device.name}
                      </Typography>
                      {device.type && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {device.type}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={device.active !== false ? "Active" : "Inactive"}
                      size="small"
                      color={device.active !== false ? "success" : "default"}
                      variant={device.active !== false ? "filled" : "outlined"}
                    />
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => { setEditingDevice(device); setDeviceDialogOpen(true); }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deleteDeviceMutation.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete "${device.name}"? This cannot be undone.`)) {
                            deleteDeviceMutation.mutate(device.id);
                          }
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {/* Task templates */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <SectionCard
            title="Task templates"
            description={`${templates.length} template${templates.length !== 1 ? "s" : ""}`}
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => { setEditingTemplate(null); setTemplateDialogOpen(true); }}
              >
                Add template
              </Button>
            }
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search templates…"
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
                endAdornment: templateSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setTemplateSearch("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ mb: 2 }}
            />

            <Divider />

            {templatesQuery.isLoading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Stack>
            ) : templatesQuery.isError ? (
              <Alert severity="error">Failed to load templates.</Alert>
            ) : templates.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                <AssignmentOutlinedIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2">No templates yet.</Typography>
                <Typography variant="caption">Click "Add template" to create one.</Typography>
              </Box>
            ) : filteredTemplates.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">No results for "{templateSearch}"</Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />} spacing={0}>
                {filteredTemplates.map((template) => (
                  <Stack
                    key={template.id}
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ py: 1.25 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" mb={0.25}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {template.name}
                        </Typography>
                        {template.category && (
                          <Chip label={template.category} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                        )}
                      </Stack>
                      {template.description && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {template.description}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={template.active !== false ? "Active" : "Inactive"}
                      size="small"
                      color={template.active !== false ? "success" : "default"}
                      variant={template.active !== false ? "filled" : "outlined"}
                    />
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => { setEditingTemplate(template); setTemplateDialogOpen(true); }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deleteTemplateMutation.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete "${template.name}"? This cannot be undone.`)) {
                            deleteTemplateMutation.mutate(template.id);
                          }
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <DeviceDialog
        open={deviceDialogOpen}
        initial={editingDevice}
        onClose={() => { setDeviceDialogOpen(false); setEditingDevice(null); }}
        onSave={(payload) => {
          if (editingDevice) {
            updateDeviceMutation.mutate({ deviceId: editingDevice.id, payload });
          } else {
            createDeviceMutation.mutate(payload);
          }
        }}
        saving={createDeviceMutation.isPending || updateDeviceMutation.isPending}
      />

      <TemplateDialog
        open={templateDialogOpen}
        initial={editingTemplate}
        onClose={() => { setTemplateDialogOpen(false); setEditingTemplate(null); }}
        onSave={(payload) => {
          if (editingTemplate) {
            updateTemplateMutation.mutate({ templateId: editingTemplate.id, payload });
          } else {
            createTemplateMutation.mutate({ ...payload, applies_to: "participant" });
          }
        }}
        saving={createTemplateMutation.isPending || updateTemplateMutation.isPending}
      />
    </Box>
  );
};
